import React, {useCallback, useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {useAssessmentsContext} from '../context/Assessments.context'
import {Badge, Button, Col, Dropdown, OverlayTrigger, Row, Tooltip} from 'react-bootstrap'
import {Timeline} from '../../../../../components/Timeline'
import AssessmentTimeLineTitle from './AssessmentsTimeLineTitle'
import AssessmentsCard from './AssessmentsCard'
import AddAssessment from './AddAssessment'
import axios from '../../../../../utils/apiClient'
import isEmpty from 'is-empty'
import SortMenu from './SortMenu'
import FilterBar from './FilterBar'
import {useAuth} from '../../../../../context/authContext'

const componentName = 'AssessmentsTab'
const baseURL = process.env.REACT_APP_REST_API_BASE_URL

const buildAssessmentTitle = (assessment, idx) => (
  <AssessmentTimeLineTitle
    assessment={assessment}
    idx={idx}
  />
)

const buildAssessmentBody = (assessment, idx, openStates, toggleIndividual) => (
  <AssessmentsCard
    toggleIndividual={() => toggleIndividual(idx)}
    open={openStates[idx]}
    assessment={assessment}
    idx={idx}
  />
)

export const sortItems = [
  {
    value: "Default",
    text: "Default",
    hasDivider: false,
    condition: true
  },
  {
    value: "Assessment Name (A-Z)",
    text: "Name (A-Z)",
    hasDivider: false,
    condition: true
  },
  {
    value: "Assessment Name (Z-A)",
    text: "Name (Z-A)",
    hasDivider: false,
    condition: true
  },
  {
    value: "Assessment Status (A-Z)",
    text: "Status (A-Z)",
    hasDivider: false,
    condition: true
  },
  {
    value: "Assessment Status (Z-A)",
    text: "Status (Z-A)",
    hasDivider: false,
    condition: true
  },
]

function Assessments({ patient }) {
  const {
    setPatientId,
    assessments,
    toggleAddNewAssessment,
    setMode,
    setSelectedTemplate,
    setfillAssessment,
    setIndividualAssessmentCard,
    getAssessmentMutation,
    totalSize
  } = useAssessmentsContext();
  const auth = useAuth();

  const [templateList, setTemplateList] = useState([]);
  const hasWriteAccess = auth.hasAuthority("AUTH_PD_ASSESSMENTS_WRITE_ACCESS");
  const [openStates, setOpenStates] = useState([]);
  const [expandAll, setExpandAll] = useState(false);

  const getTemplateList = () => {
    axios
      .get(baseURL + `/v1/assessments/patient-templates?patientId=${patient?.id}`, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((response) => {
        if (response.data && !isEmpty(response.data)) {
          setTemplateList(response.data);
        }
      })
  };

  useEffect(() => {
    setPatientId(patient?.id)
    getTemplateList()
    // Initialize assessments for the active patient only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patient?.id])

  const handleChangeTemplate = (template) => {
    const {templateName, templateId} = template
    setSelectedTemplate({ templateName, templateId });
    setIndividualAssessmentCard({});
    setMode("new");
    setfillAssessment(true);
    toggleAddNewAssessment(true);
  };

  const handleFilterSort = (pageNumber, filterParams, sortParams) => {
    getAssessmentMutation({
      pageNumber,
      filterParams,
      sortParams,
      patientId: patient?.id,
      pageSize: 20
    })
  }

  useEffect(() => {
    if (assessments) {
      setOpenStates(prevStates => {
        const newStates = new Array(assessments.length).fill(expandAll);
        prevStates.forEach((state, index) => {
          if (index < newStates.length) {
            newStates[index] = state;
          }
        });
        return newStates;
      });
    }
  }, [assessments, expandAll])

  const toggleAll = (state) => {
    setExpandAll(state)
    setOpenStates(prev => prev.map(() => state));
  };

  const toggleIndividual = useCallback((index) => {
    setOpenStates(prev => prev.map((state, i) => i === index ? !state : state));
  }, []);

  const buildTimelineBody = useCallback(
    (assessment, idx) => buildAssessmentBody(assessment, idx, openStates, toggleIndividual),
    [openStates, toggleIndividual]
  );

  return (
    <>
      <Row className="align-items-center px-3">
        <Col className="d-flex">
          <FilterBar handleFilterSort={handleFilterSort} />
          <SortMenu items={sortItems} refreshPatientList={handleFilterSort} />
        </Col>
        <Col className="text-end pe-0">
          <Dropdown className="dropdown-end">
            <Dropdown.Toggle
              variant="primary"
              id={`${componentName}-AssessmentsActions`}
              disabled={!hasWriteAccess}
              style={{
                backgroundColor: hasWriteAccess ? '#00788E' : '#CBCDCE',
                border: 'none',
                fontWeight: '500'
              }}
            >Add New Assessment
            </Dropdown.Toggle>
            <Dropdown.Menu align='end' className="dropdown-menu-aligned">
              {templateList.map((template, templateIdx) => {
                const templateKey = template.id ?? template.templateId ?? template.templateName ?? templateIdx;

                return <Dropdown.Item
                  id={`${componentName}-actionsBtnItems-${template?.id}`}
                  key={templateKey}
                  onClick={e => handleChangeTemplate(template)}
                >{template.templateName}</Dropdown.Item>
              })}
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>
      <Row className='align-items-baseline'>
        <Col>
            <Badge
              bg="light"
              text="dark"
              id={`assessmentsEventsCount`}
              className="border badge-pill mt-4">
              {totalSize}{" "}{"Assessment"}{totalSize === 1 ? '' : 's'}
            </Badge>
        </Col>
        <Col md={6} className='text-end'>
          <OverlayTrigger
            placement="auto"
            overlay={
              <Tooltip>
                Expand / Collapse All
              </Tooltip>
            }
          >
            <Button
              type="button"
              variant="light"
              className="border text-dark badge badge-pill mt-2 font-sm"
              onClick={() => toggleAll(!expandAll)}
              style={{maxWidth: 'fit-content'}}
            >
              <i className='fas fa-angle-double-up'/>
              <i className='fas fa-angle-double-down'/>
            </Button>
          </OverlayTrigger>
        </Col>
      </Row>
      <AddAssessment />
      <Row className={`${componentName}-view`}>
        <Timeline
          items={assessments}
          titleBuilder={buildAssessmentTitle}
          bodyBuilder={buildTimelineBody}/>
      </Row>
    </>
  )
}

Assessments.propTypes = {
  patient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  }).isRequired
}

export default Assessments
